/**
 * Ultratech's corridor arrivals, styled as the same dark Solari split-flap board as
 * MBMT's depot board (busOps/MbmtBoard.tsx) -- same theatre, same reason: a flap ripple
 * pulls the eye to the row that just landed.
 *
 * MBMT has a TIMS schedule to grade a truck against (on time / late). Ultratech has none,
 * so there is nothing to be "on time" against -- what this board shows instead is the
 * one thing that IS measured here: how long the transit actually took (TAT) against the
 * corridor's own derived target, not a promised one. See SHADOW-RUN.md's own note that
 * these transit targets are derived from observation, not agreed -- the "Performance"
 * column says exactly that, never "on time"/"late".
 */
import { useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'
import { EmptyState } from '../cc/EmptyState'
import { AMBER, FlapText, GREEN, MUTED, RED } from '../cc/splitFlap'
import { useFleet } from '../state/store'
import { useCan, useSiteAccess } from '../auth/useCan'
import { arrivalStripMinutes, onArrivalStrip } from '../lib/arrivalWatch'

interface TransitRow {
  kind: string
  vehicle_id: string
  route_id: string | null
  from_hub_code: string | null
  hub_code: string
  event_ts: string
  target_min: number | null
  actual_min: number | null
  breached: boolean | null
  breach_min: number | null
  variance_min: number | null
  trip_id: string | null
}

interface GeotagEvent {
  kind: string
  ts: number | null
  event: string | null
  geofence_name: string | null
  hub_code: string | null
  lat: number | null
  lon: number | null
}

interface TripReplay {
  events: GeotagEvent[]
  geotag_count: number
}

const HUB_LABEL: Record<string, string> = { DHAR: 'Dhar', JHUL: 'Jhulwania', DHULE: 'Dhule' }

/** Trucks close enough that a gate/loading crew should already be watching for them. */
const ARRIVING_SOON_MIN = 30

function hhmm(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-IN', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Asia/Kolkata',
  })
}

function durationText(min: number | null): string {
  if (min == null) return '—'
  const m = Math.round(min)
  return m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`
}

/** Derived-target performance, never schedule language -- there is no schedule here. */
function performanceText(r: TransitRow): { text: string; tone: string } {
  if (r.target_min == null || r.variance_min == null) return { text: 'no target yet', tone: MUTED }
  if (!r.breached) {
    const under = Math.round(-r.variance_min)
    return { text: under > 0 ? `${under}m under` : 'on target', tone: GREEN }
  }
  return { text: `+${Math.round(r.breach_min ?? r.variance_min)}m`, tone: RED }
}

const ROW_GRID = 'grid grid-cols-[90px_116px_minmax(140px,1fr)_84px_140px] items-center gap-x-4'
const INBOUND_GRID =
  'grid grid-cols-[116px_minmax(120px,1fr)_90px_120px_90px_90px_84px] items-center gap-x-4'

function socColor(pct: number | null): string {
  if (pct == null) return MUTED
  if (pct < 20) return RED
  if (pct < 35) return AMBER
  return GREEN
}

export function ArrivalBoard() {
  const [rows, setRows] = useState<TransitRow[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)
  const [site, setSite] = useState('ALL')
  const [now, setNow] = useState(() => Date.now())
  const [expandedTripId, setExpandedTripId] = useState<string | null>(null)
  const [geotags, setGeotags] = useState<GeotagEvent[]>([])
  const [geotagsLoading, setGeotagsLoading] = useState(false)
  const [geotagsError, setGeotagsError] = useState<string | null>(null)
  const selectVehicle = useFleet((s) => s.selectVehicle)
  const flyTo = useFleet((s) => s.flyTo)
  const vehicles = useFleet((s) => s.vehicles)
  // /api/reports/tat_transit is gated server-side by the reports grant + ultratech site --
  // call both hooks unconditionally (Rules of Hooks) and AND the results.
  const hasTransitResourceGrant = useCan('reports')
  const hasTransitSiteGrant = useSiteAccess('ultratech')
  const canLoadTransit = hasTransitResourceGrant && hasTransitSiteGrant

  useEffect(() => {
    if (!canLoadTransit) {
      // Department lacks the reports grant (or the ultratech site) -- the server would
      // 403 this. Degrade the same way arrival_board's own fetch below already does by
      // design: no error banner, the board just shows no transit rows.
      setLoaded(true)
      setError(null)
      setRows([])
      return
    }
    let cancelled = false
    const load = async () => {
      try {
        // Server-filtered to kind='transit' -- dwell events fire far more often, so
        // paging the shared kind-mixed 'tat' view and filtering client-side could go
        // entire fetches without a single transit row. See serving/app.py's REPORTS.
        const r = await fetch('/api/reports/tat_transit?limit=60')
        const body = await r.json() as { rows?: TransitRow[]; error?: string }
        if (cancelled) return
        if (!r.ok || body.error) throw new Error(body.error ?? `arrivals ${r.status}`)
        setRows(body.rows ?? [])
        setError(null)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setLoaded(true)
      }
    }
    void load()
    // Same cadence as MBMT's board and the ETA table this replaced.
    const id = setInterval(() => void load(), 20_000)
    return () => { cancelled = true; clearInterval(id) }
  }, [canLoadTransit])

  // CommandCentre.tsx (this board's own parent -- see DetailWorkspace.tsx) already polls
  // /api/reports/arrival_board every 20s and writes it into the shared store for exactly
  // this reason (see store.ts's own comment on arrivalBoard, and CommandCentre's "the map
  // and this board never quote different distances"). A second independent poll of the
  // same endpoint here was pure duplication -- confirmed live, same rows fetched twice
  // every 20s for as long as this tab stayed open.
  const arrivalBoardMap = useFleet((s) => s.arrivalBoard)
  const inbound = useMemo(() => Array.from(arrivalBoardMap.values()), [arrivalBoardMap])

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1_000)
    return () => clearInterval(id)
  }, [])

  const go = (vehicleId: string) => {
    selectVehicle(vehicleId)
    const v = vehicles.get(vehicleId)
    if (v?.lat != null && v?.lon != null) flyTo({ lon: v.lon, lat: v.lat, zoom: 13 })
  }

  // The last 5 geotags (ENTER/EXIT geofence crossing) for this one leg -- direct
  // instruction ("last 5 geo tags must be shown"), not the whole trail (a long leg can
  // carry dozens, which is what the map replay CommandCentre drives off the same
  // endpoint is for). Events arrive chronological ascending, so the last 5 in the array
  // are the most recent 5, not an arbitrary slice. A second click on the same row
  // collapses it rather than re-fetching.
  const toggleGeotags = async (r: TransitRow) => {
    if (expandedTripId === r.trip_id) {
      setExpandedTripId(null)
      return
    }
    setExpandedTripId(r.trip_id)
    setGeotags([])
    setGeotagsError(null)
    if (!r.trip_id) {
      setGeotagsError('This leg predates trip-id tracking -- no geotags to show.')
      return
    }
    setGeotagsLoading(true)
    try {
      const res = await fetch(`/api/trips/${encodeURIComponent(r.trip_id)}/replay`)
      const body = await res.json() as TripReplay & { error?: string }
      if (!res.ok || body.error) throw new Error(body.error ?? `replay ${res.status}`)
      setGeotags(body.events.filter((e) => e.kind === 'geotag').slice(-5))
    } catch (e) {
      setGeotagsError(e instanceof Error ? e.message : String(e))
    } finally {
      setGeotagsLoading(false)
    }
  }

  // Fixed, not derived from what happens to be in the current row window -- a quiet hub
  // (no transit legs in the last 60 rows) still needs its own filter button, not a
  // silently-missing one that looks broken.
  const sites = ['ALL', 'DHAR', 'DHULE', 'JHUL']

  // The pill badge is a crew's "how many do I need to watch for" count, not a history
  // count -- so it tracks the same ARRIVING_SOON_MIN window as the strip below, not the
  // Arrived list.
  const counts = useMemo(() => {
    const soon = inbound.filter((r) => onArrivalStrip(r, ARRIVING_SOON_MIN))
    const c = new Map<string, number>([['ALL', soon.length]])
    for (const r of soon) c.set(r.hub_code, (c.get(r.hub_code) ?? 0) + 1)
    return c
  }, [inbound])

  const filtered = useMemo(
    () => (site === 'ALL' ? rows : rows.filter((r) => r.hub_code === site)),
    [rows, site],
  )

  // Only trucks close enough to matter -- everything further out is what the "Arrived"
  // list below was five minutes ago, not something a crew needs to watch for yet.
  const arrivingSoon = useMemo(() => {
    const soon = inbound.filter((r) =>
      onArrivalStrip(r, ARRIVING_SOON_MIN)
      && (site === 'ALL' || r.hub_code === site))
    return soon
      .slice()
      .sort((a, b) => (arrivalStripMinutes(a) ?? 0) - (arrivalStripMinutes(b) ?? 0))
  }, [inbound, site])

  // NOT h-full/min-h-0: this board is mounted inside DetailWorkspace's own
  // `min-h-0 flex-1 overflow-auto` tab content div (every sibling panel -- FleetGrid,
  // AlertsGrid etc -- renders into that same single scroll container at its natural
  // height, nothing fancier). Giving this root ITS OWN `h-full` and the "Arrived" list
  // below its OWN `flex-1 overflow-auto` stacked TWO independent scroll/height-negotiating
  // containers where the parent already provides one -- the classic nested-flex-scroll
  // conflict, and the kind that is invisible at 100% browser zoom (where subpixel
  // rounding in the height cascade happens to land cleanly) and clips rows with no
  // visible scrollbar at other zoom levels, which is exactly what was reported. Letting
  // this render at its natural height, same as every sibling panel, removes the conflict
  // outright rather than chasing the exact zoom factor it misbehaves at.
  return (
    <div className="flex flex-col overflow-hidden rounded-[18px] border border-[rgba(15,23,42,0.2)] bg-[#0B1220] shadow-[0_18px_44px_-24px_rgba(2,6,23,0.9)]">
      <header className="flex shrink-0 items-center gap-4 border-b border-white/[0.07] px-5 pb-3.5 pt-4">
        <div>
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-[#FBBF24]">
            Corridor · arrivals
          </p>
          <p className="mt-1 text-[11px] text-[#64748B]">
            Live telemetry · transit TAT, no schedule to grade against
          </p>
        </div>
        <div className="ml-auto flex items-center gap-4">
          <div className="flex gap-1">
            {sites.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSite(s)}
                className={clsx(
                  'rounded-full px-3 py-1 text-[11px] font-semibold transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FBBF24]',
                  site === s
                    ? 'bg-[#FBBF24] text-[#0B1220]'
                    : 'text-[#94A3B8] hover:bg-white/[0.06] hover:text-[#E8EDF6]',
                )}
              >
                {s === 'ALL' ? 'All' : HUB_LABEL[s] ?? s} {counts.get(s) ?? 0}
              </button>
            ))}
          </div>
          <FlapText
            text={new Date(now).toLocaleTimeString('en-IN', { hour12: false, timeZone: 'Asia/Kolkata' })}
            width={8}
            tone={AMBER}
          />
        </div>
      </header>

      <div className="shrink-0 border-b border-white/[0.07] px-5 pb-2 pt-3">
        <p className="mb-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#34D399]">
          Arriving soon · within {ARRIVING_SOON_MIN}m{site !== 'ALL' ? ` · ${HUB_LABEL[site] ?? site}` : ''}
        </p>
        {arrivingSoon.length === 0 ? (
          // A hub with genuinely nothing inbound right now and a hub whose board is
          // broken look identical if this section just disappears -- same reasoning as
          // v_arrival_readiness's own "can the board be trusted" check in
          // db/reports_arrival.sql. Say so explicitly rather than vanish.
          <p className="pb-1 text-[11px] text-[#64748B]">
            Nothing within {ARRIVING_SOON_MIN}m of {site === 'ALL' ? 'any hub' : (HUB_LABEL[site] ?? site)} right now.
          </p>
        ) : (
          <>
          <div className={clsx(INBOUND_GRID, 'pb-1')}>
            {['Vehicle', 'Hub', 'SoC now', 'Est. SoC → arrival', 'Speed', 'Remaining', 'ETA'].map((h) => (
              <span key={h} className="font-mono text-[9px] font-medium uppercase tracking-[0.16em] text-[#526079]">
                {h}
              </span>
            ))}
          </div>
          <div className="space-y-1">
            {arrivingSoon.map((r) => (
              <button
                key={r.vehicle_id}
                type="button"
                onClick={() => go(r.vehicle_id)}
                className={clsx(
                  INBOUND_GRID,
                  'w-full rounded-[8px] px-1 py-1.5 text-left transition-colors',
                  'hover:bg-white/[0.05] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FBBF24]',
                )}
              >
                <span className="font-mono text-[12px] font-semibold tracking-tight text-[#E8EDF6]">
                  {r.vehicle_id}
                </span>
                <span className="truncate text-[11px] font-medium text-[#C7D0DE]">
                  {HUB_LABEL[r.hub_code] ?? r.hub_code}
                </span>
                <span className="font-mono text-[12px] tabular-nums" style={{ color: socColor(r.soc_pct) }}>
                  {r.soc_pct != null ? `${Math.round(r.soc_pct)}%` : '—'}
                </span>
                <span className="font-mono text-[12px] tabular-nums" style={{ color: socColor(r.expected_soc_pct ?? null) }}>
                  {r.expected_soc_pct != null ? `~${Math.round(r.expected_soc_pct)}%` : '—'}
                </span>
                <span className="font-mono text-[12px] tabular-nums text-[#C7D0DE]">
                  {r.speed_now_kph != null ? `${Math.round(r.speed_now_kph)} kph` : '—'}
                </span>
                <span className="font-mono text-[12px] tabular-nums text-[#C7D0DE]">
                  {Math.round(r.km_to_go)} km
                </span>
                <span
                  className="font-mono text-[12px] font-semibold tabular-nums text-[#FBBF24]"
                  title={r.confidence}
                >
                  {r.measured_eta_min != null ? `${r.measured_eta_min}m` : '—'}
                </span>
              </button>
            ))}
          </div>
          </>
        )}
      </div>

      {/* "Arrived" below is a DIFFERENT metric than the pills/strip above -- backward-
          looking completed transit legs (tat_transit), not the forward-looking ETA<=30m
          inbound count the pills and "Arriving soon" use. They were never meant to tally;
          the subtitle says so explicitly rather than let a reader assume they should. */}
      <p className="shrink-0 px-5 pb-0.5 pt-2 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-[#526079]">
        Arrived
      </p>
      <p className="shrink-0 px-5 pb-1 text-[10.5px] text-[#526079]">
        Completed legs -- a separate count from the pills above, not a running total of them.
      </p>
      <div className={clsx(ROW_GRID, 'shrink-0 border-b border-white/[0.07] px-5 py-2')}>
        {['Time', 'Vehicle', 'Route', 'TAT', 'Performance'].map((h) => (
          <span key={h} className="font-mono text-[9.5px] font-medium uppercase tracking-[0.18em] text-[#526079]">
            {h}
          </span>
        ))}
      </div>

      <div className="px-2 py-1">
        {!loaded && (
          <div className="space-y-2 p-3">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-11 animate-pulse rounded-[10px] bg-white/[0.05]" />
            ))}
          </div>
        )}
        {loaded && error && (
          <div className="[&_*]:!text-[#94A3B8]">
            <EmptyState title="Arrivals unavailable" body={`Report error: ${error}`} />
          </div>
        )}
        {loaded && !error && filtered.length === 0 && (
          <div className="[&_*]:!text-[#94A3B8]">
            <EmptyState
              title="No transit legs yet"
              body="Rows appear here as trucks complete a road leg between hubs."
            />
          </div>
        )}
        {filtered.map((r, i) => {
          const perf = performanceText(r)
          const rowKey = `${r.vehicle_id}-${r.event_ts}-${i}`
          const isExpanded = r.trip_id != null && expandedTripId === r.trip_id
          return (
            <div key={rowKey}>
              <button
                type="button"
                onClick={() => {
                  go(r.vehicle_id)
                  void toggleGeotags(r)
                }}
                className={clsx(
                  ROW_GRID,
                  'w-full animate-row-in rounded-[10px] px-3 py-2 text-left transition-colors',
                  'border-b border-white/[0.04] last:border-b-0',
                  isExpanded ? 'bg-white/[0.06]' : 'hover:bg-white/[0.05]',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FBBF24]',
                )}
              >
                <FlapText text={hhmm(r.event_ts)} width={5} />
                <span className="font-mono text-[13px] font-semibold tracking-tight text-[#E8EDF6]">
                  {r.vehicle_id}
                </span>
                <span className="truncate text-[12px] font-medium text-[#C7D0DE]">
                  {HUB_LABEL[r.from_hub_code ?? ''] ?? r.from_hub_code ?? '—'}
                  {' → '}
                  {HUB_LABEL[r.hub_code] ?? r.hub_code}
                </span>
                <span className="font-mono text-[12px] tabular-nums text-[#C7D0DE]">
                  {durationText(r.actual_min)}
                </span>
                <FlapText text={perf.text} width={11} tone={perf.tone} small stagger={18} />
              </button>
              {isExpanded && (
                <div className="mb-1 rounded-[8px] border border-white/[0.07] bg-white/[0.03] px-3 py-2">
                  <p className="mb-1.5 font-mono text-[9px] font-semibold uppercase tracking-[0.16em] text-[#526079]">
                    Last 5 geotags for this leg
                  </p>
                  {geotagsLoading && (
                    <p className="text-[11px] text-[#64748B]">Loading…</p>
                  )}
                  {!geotagsLoading && geotagsError && (
                    <p className="text-[11px] text-[#94A3B8]">{geotagsError}</p>
                  )}
                  {!geotagsLoading && !geotagsError && geotags.length === 0 && (
                    <p className="text-[11px] text-[#64748B]">No geofence crossings recorded for this leg.</p>
                  )}
                  {!geotagsLoading && !geotagsError && geotags.length > 0 && (
                    <div className="space-y-0.5">
                      {geotags.map((g, gi) => (
                        <div
                          key={`${g.ts}-${gi}`}
                          className="grid grid-cols-[64px_60px_minmax(120px,1fr)_1fr] gap-x-3 font-mono text-[11px] tabular-nums text-[#C7D0DE]"
                        >
                          <span>{g.ts != null ? hhmm(new Date(g.ts).toISOString()) : '—'}</span>
                          <span className={g.event === 'ENTER' ? 'text-[#34D399]' : 'text-[#FBBF24]'}>
                            {g.event ?? '—'}
                          </span>
                          <span className="truncate text-[#E8EDF6]">
                            {g.geofence_name ?? g.hub_code ?? '—'}
                          </span>
                          <span className="text-[#8794A7]">
                            {g.lat != null && g.lon != null
                              ? `${g.lat.toFixed(5)}, ${g.lon.toFixed(5)}`
                              : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
